import { useEffect, useMemo, useState, useContext } from 'react';
import { Button } from '@mantine/core';
import { axios } from '../../../../utils/axios';
import { useParams } from 'react-router-dom';
import MantineTable from '../../../../common/table/mantine';
import { Status } from '../../styles';
import { showAlert } from '../../../../utils/alerts';
import { CostingRequestsContext } from '../../../../contexts/CostingRequest';
import moment from 'moment';
import AssignedUsersList from '../../../../components/assignedUsersList';

const PccSetup = ({ activeTab, setLogsModal }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const { crNumber } = useParams();
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { isCrRoleAuthorized, crAssignedSpocs } = useContext(CostingRequestsContext);

  useEffect(() => {
    if (activeTab === 'pccSetup') {
      fetch();
    }
  }, [activeTab]);

  const fetch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/costing-request/costing_pcc_setup/${crNumber}`);
      const apiData = response?.data?.data;
      const updatedData = transformData(apiData[0]);
      setData(updatedData);
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData((prevData) => {
      const updatedData = prevData.map((ele) =>
        ele.id === selectedId ? { ...ele, pcc_setup: false } : ele,
      );
      return updatedData;
    });
    setSelectedId(null);
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const transformData = (pccData) => {
    const submittedOn = pccData.finish_date;
    const submittedBy = pccData.submitted_by;
    const pccList = pccData.int_cr_pcc_setup_records;

    return pccList.map((ele) => {
      const producingPlant = ele.producing_plant.trim();
      return {
        ...ele,
        producing_plant: producingPlant,
        user: crAssignedSpocs(producingPlant),
        submitted_on: moment(submittedOn).isValid()
          ? moment(submittedOn).format('DD-MM-YYYY')
          : null,
        submitted_by: submittedBy,
      };
    });
  };

  const handleRetry = async () => {
    try {
      setRetryLoading(true);

      const response = await axios.post(`/costing-request/costing_pcc_setup/${crNumber}`);

      if (response.status === 200) {
        showAlert(
          {
            message: `PCC setup has been successfully updated`,
          },
          'success',
        );
      } else {
        throw new Error(response.data.message || 'Failed to refresh costing request');
      }

      await fetch();
    } catch (error) {
      showAlert(error, 'error');
    } finally {
      setRetryLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          const plant = row.original?.producing_plant;
          const status = row.original?.status;
          const hasRefreshAccess = isCrRoleAuthorized(String(plant));

          const isDisabled =
            status === 'Closed' || status === 'Not Started' || !hasRefreshAccess || retryLoading;
          return (
            <Button
              variant='subtle'
              color='dark'
              size='sm'
              style={{ width: '100%' }}
              onClick={handleRetry}
              disabled={isDisabled}
            >
              {'Retry'}
            </Button>
          );
        },
      },
      {
        accessorKey: 'sku',
        header: 'SKU No',
      },
      {
        accessorKey: 'sku_description',
        header: 'SKU Desc',
      },
      {
        accessorKey: 'producing_plant',
        header: 'Producing Plant',
      },
      {
        accessorKey: 'profit_center',
        header: 'Profit Center',
      },
      {
        accessorKey: 'order_type',
        header: 'Order Type',
      },
      {
        accessorKey: 'production_version',
        header: 'Production Version',
      },
      {
        id: 'status',
        header: 'Status',
        Cell: ({
          row: {
            original: { status },
          },
        }) => {
          return status ? <Status status={status} /> : null;
        },
      },
      {
        id: 'logs',
        header: 'Logs',
        Cell: ({ row }) => (
          <Button
            color='dark'
            onClick={() =>
              setLogsModal({
                open: true,
                logs: row.original.int_cr_pcc_logs,
                type: 'PCC Setup',
              })
            }
          >
            View Logs
          </Button>
        ),
      },
      {
        accessorKey: 'user',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { user },
          },
        }) => <AssignedUsersList users={user} />,
      },
      {
        accessorKey: 'submitted_on',
        header: 'Submitted On',
      },
    ],
    [handleClose, data, setData],
  );

  const initialState = {
    columnPinning: {
      left: [],
      right: [],
    },
    sorting: [
      {
        id: 'int_cr_sku_selector.sku_description',
        desc: false,
      },
    ],
  };

  return (
    <>
      <MantineTable
        columns={columns}
        initialData={data.filter((ele) => ele)}
        key={refreshKey}
        enablePinning
        initialState={initialState}
        unique={'id'}
        enableRowSelection={false}
        loading={loading || retryLoading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        pageSize={10}
        enableSelectAll={false}
        csvFilename={`pcc_setup_${moment().format('YYYY-MM-DD')}`}
      />
    </>
  );
};

export default PccSetup;
